import {
  ChainType,
  chainTypeEntries,
  forEachChainType,
  makeChainTypeRecord,
  makePartialChainTypeRecord,
} from '@polaris/core/models'
import { useCallback, useMemo } from 'react'
import { useCosmosWalletOptions } from '@/src/ecosystems/cosmos/use-wallet-options'
import { useEvmWalletOptions } from '@/src/ecosystems/evm/use-wallet-options'
import { useSolanaWalletOptions } from '@/src/ecosystems/solana/use-wallet-options'
import { useVaultWalletOptions } from '@/src/ecosystems/vault/use-wallet-options'

/** Base interface for wallet info intended for display in the UI. */
export interface WalletOption {
  /** Raw ID coming from provider. Not used internally for unique identification. */
  id: string
  /**
   * NOTE: This is used as a case-sensitive key! Must be stable across ecosystems from the same wallet, and over time.
   */
  displayName: string
  logoUrl: string
  website?: string
  /**
   * If included, indicates that the wallet supports ONLY the given chains.
   *
   * Chain IDs must be of format `chainType:chainId`
   */
  onlySupportsChains?: string[]
}

/**
 * A connector is a function that connects a wallet to a chain.
 * It returns a promise that resolves when the wallet is connected.
 */
type WalletConnector = (params?: {
  onRetry?: () => Promise<void>
  logEvent?: boolean
}) => Promise<void>

/**
 * A connectable wallet option is a wallet that can be connected to the dapp.
 * It includes a connector function that connects the wallet to a chain.
 * This object will also emit updates to `reportConnectingWalletInfo` if deemed useful.
 */
export interface ConnectableWalletOption extends WalletOption {
  connect: WalletConnector
}

/**
 * A connected wallet option is a wallet that is currently connected to the dapp.
 * It includes a disconnect function that disconnects the wallet from the dapp.
 */
export interface ConnectedWalletOption extends WalletOption {
  /** If supported by the wallet, the name the user set on the account. */
  accountName?: string
  disconnect: () => void
}

/**
 * Wallet options aggregated across multiple ecosystems.
 * In this case, connect and disconnect apply to all aggregated wallet options.
 * Otherwise, mappings by chain type are available for connecting and disconnecting.
 */
export interface AggregatedWalletOption extends WalletOption {
  connectors: Partial<Record<ChainType, WalletConnector>>
  disconnectors: Partial<Record<ChainType, () => void>>
  /** At least 1 ecosystem **can be connected**. Intersects with `ConnectableWalletOption`. */
  connect?: WalletConnector
  /** At least 1 ecosystem **is connected**. Intersects with `ConnectedWalletOption`. */
  disconnect?: () => void
}

/**
 * Collection of wallet options for a single ecosystem such as Cosmos, EVM, Solana
 */
export interface EcosystemWalletOptions {
  chainType: ChainType
  ecosystemName: string
  ecosystemImgUrl: string
  connectedWalletOption: ConnectedWalletOption | undefined
  connectableOptions: ConnectableWalletOption[]
  installableWalletOptions?: WalletOption[]
  enabled: boolean
  /** Is initialized. Not related to connection status. */
  ready: boolean
}

/**
 * A connecting wallet is a wallet that is in the process of connecting to the dapp.
 * It includes an icon, name, type, status, and error if the connection failed.
 */
export interface ConnectingWallet {
  icon: string
  name: string
  type: ChainType
  status: 'connecting' | 'error'
  error?: {
    type: 'rejected' | 'unexpected' | 'connection-already-in-progress'
    onRetry: () => Promise<void>
  }
}

export interface WalletOptionsProps {
  searchWalletQuery?: string
  /**
   * Optionally accept updates and accept utilities related to currently connecting wallet lifecycle.
   * Useful for updating the UI when a wallet is in the process of connecting or has an error.
   * IMPORTANT: must be memoized.
   */
  reportConnectingWalletInfo?: (info: ConnectingWallet) => void
}

/**
 * Aggregates wallet options and connection management across multiple blockchain ecosystems.
 *
 * This hook provides a unified interface for interacting with wallet options
 * for different ecosystems such as Cosmos, EVM, Solana. It allows
 * for connecting, disconnecting, and retrieving display information for wallets.
 *
 * Note: The returned object is memoized, ensuring stable references across
 * wallet and chain changes, maintaining consistency in wallet management.
 */
export const useWalletOptions = (props: WalletOptionsProps = {}) => {
  const cosmosWalletOptions = useCosmosWalletOptions(props)
  const evmWalletOptions = useEvmWalletOptions(props)
  const solanaWalletOptions = useSolanaWalletOptions(props)
  const vaultWalletOptions = useVaultWalletOptions()

  const getWalletOptions = useCallback(
    (chainType: ChainType) => {
      switch (chainType) {
        case ChainType.Cosmos:
          return cosmosWalletOptions
        case ChainType.Evm:
          return evmWalletOptions
        case ChainType.Solana:
          return solanaWalletOptions
        case ChainType.Bitcoin:
          throw new Error(
            '[getWalletOptions] Bitcoin wallet integration is suspended.', // https://linear.app/osmosis/issue/POLFE-1121/remove-bitcoin-connector
          )
        case ChainType.Namada:
          return vaultWalletOptions
        default:
          throw new Error(
            `[getWalletOptions] Unsupported chain type: ${chainType}`,
          )
      }
    },
    [
      cosmosWalletOptions,
      evmWalletOptions,
      solanaWalletOptions,
      vaultWalletOptions,
    ],
  )

  const disconnect = useCallback(
    (chainType: ChainType) =>
      getWalletOptions(chainType)?.connectedWalletOption?.disconnect(),
    [getWalletOptions],
  )

  const disconnectAll = useCallback(
    () => forEachChainType(disconnect),
    [disconnect],
  )

  const ecosystemWallets = useMemo(
    () => makeChainTypeRecord(getWalletOptions),
    [getWalletOptions],
  )

  const connectedWallets = useMemo(
    () =>
      makePartialChainTypeRecord(
        (chainType) => getWalletOptions(chainType)?.connectedWalletOption,
      ),
    [getWalletOptions],
  )

  const connectableWallets = useMemo(
    () =>
      makePartialChainTypeRecord(
        (chainType) => getWalletOptions(chainType)?.connectableOptions,
      ),
    [getWalletOptions],
  )

  const installableWallets = useMemo(
    () =>
      makeChainTypeRecord(
        (chainType) => getWalletOptions(chainType)?.installableWalletOptions,
      ),
    [getWalletOptions],
  )

  const aggregatedWallets = useMemo(() => {
    /** Wallet ID > Aggregated Wallet Option */
    const wallets = new Map<string, AggregatedWalletOption>()

    forEachChainType((chainType) => {
      const walletOptions = getWalletOptions(chainType)

      if (walletOptions.enabled) {
        const noSearchQuery =
          !props.searchWalletQuery || props.searchWalletQuery.trim() === ''

        //connected wallets should only be shown when the user isn't actively searching.
        if (walletOptions.connectedWalletOption && noSearchQuery) {
          aggregateWallet(
            wallets,
            chainType,
            walletOptions.connectedWalletOption,
          )
        }

        for (const wallet of walletOptions.connectableOptions) {
          aggregateWallet(wallets, chainType, wallet)
        }
      }
    })

    return Array.from(wallets.values()).sort(
      (a, b) =>
        // Sort connected wallets to the front
        Object.values(b.disconnectors).length -
        Object.values(a.disconnectors).length,
    )
  }, [getWalletOptions, props.searchWalletQuery])

  const ready = useMemo(
    () => makeChainTypeRecord((chainType) => getWalletOptions(chainType).ready),
    [getWalletOptions],
  )
  const allReady = Object.values(ready).every(Boolean)

  return useMemo(
    () => ({
      ecosystemWallets,
      connectableWallets,
      connectedWallets,
      installableWallets,
      aggregatedWallets,
      ready,
      allReady,
      disconnect,
      disconnectAll,
    }),
    [
      ecosystemWallets,
      connectableWallets,
      connectedWallets,
      installableWallets,
      aggregatedWallets,
      ready,
      allReady,
      disconnect,
      disconnectAll,
    ],
  )
}

export type UseWalletOptionsReturn = ReturnType<typeof useWalletOptions>

const aggregateWallet = (
  wallets: Map<string, AggregatedWalletOption>,
  chainType: ChainType,
  wallet: ConnectedWalletOption | ConnectableWalletOption,
) => {
  let displayName = wallet.displayName
  const split = displayName.split(' ')
  if (split.length > 1 && split[1].toLowerCase() === 'wallet') {
    displayName = split[0]
  }
  const existingWallet = wallets.get(displayName)

  if (existingWallet) {
    const connectors =
      'connect' in wallet && wallet.connect !== undefined
        ? {
            ...existingWallet.connectors,
            [chainType]: wallet.connect,
          }
        : existingWallet.connectors

    const disconnectors =
      'disconnect' in wallet && wallet.disconnect !== undefined
        ? {
            ...existingWallet.disconnectors,
            [chainType]: wallet.disconnect,
          }
        : existingWallet.disconnectors

    /**
     * Connects all connectors serially (in order, awaiting each one).
     */
    const serial = async (entries: [ChainType, WalletConnector][]) => {
      for (const [_, connector] of entries) {
        await connector()
      }
    }

    /**
     * Connects all connectors concurrently (in parallel).
     */
    const concurrent = async (entries: [ChainType, WalletConnector][]) => {
      await Promise.all(entries.map(([_, connector]) => connector()))
    }

    /**
     * List of wallet displayNames that support concurrent connect.
     * Which, ideally, means all the ecosystems can be approved in one popup.
     * Add more wallet names here as needed.
     */
    const CONCURRENT_WALLETS: string[] = ['Leap']

    wallets.set(displayName, {
      ...existingWallet,
      ...wallet,
      id: `${existingWallet.id}-${wallet.id}:${chainType}`,
      displayName,
      connectors,
      disconnectors,
      connect:
        Object.values(connectors).length > 0
          ? async () => {
              // There's an ongoing bug with MetaMask where, if you try to connect
              // Solana with EVM, the EVM connector becomes disconnected.
              // This is a temp workaround to focus on just connecting EVM first, and allowing the user to add
              // a Solana connection later.
              const entries =
                displayName === 'MetaMask'
                  ? chainTypeEntries(connectors).filter(
                      ([chainType]) => chainType === ChainType.Evm,
                    )
                  : chainTypeEntries(connectors)
              if (CONCURRENT_WALLETS.includes(displayName)) {
                await concurrent(entries)
              } else {
                await serial(entries)
              }
            }
          : undefined,
      disconnect:
        Object.values(disconnectors).length > 0
          ? () => {
              for (const disconnector of Object.values(disconnectors)) {
                disconnector()
              }
            }
          : undefined,
    })
  } else {
    let connectors: AggregatedWalletOption['connectors'] = {}
    let connect: AggregatedWalletOption['connect']
    let disconnectors: AggregatedWalletOption['disconnectors'] = {}
    let disconnect: AggregatedWalletOption['disconnect']

    if ('connect' in wallet && wallet.connect !== undefined) {
      connectors = {
        [chainType]: wallet.connect,
      }
      connect = wallet.connect
    }

    if ('disconnect' in wallet && wallet.disconnect !== undefined) {
      disconnectors = {
        [chainType]: wallet.disconnect,
      }
      disconnect = wallet.disconnect
    }

    wallets.set(displayName, {
      ...wallet,
      id: `${wallet.id}:${chainType}`,
      displayName,
      connectors,
      disconnectors,
      connect,
      disconnect,
    })
  }
}
